import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { useIntl } from '@leagueplatform/locales';
import { HeaderNavLink } from '@leagueplatform/solutions-foundation';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { HIDDEN_CAPABILITIES_STORAGE_KEY } from 'common/persona-selector-signals';
import { useIsCaregiverShell } from 'hooks/use-is-caregiver.hook';

type NavLinkEntry = HeaderNavLink & { capabilityKey?: string };

function getHiddenCapabilities(): string[] {
  try {
    const raw = sessionStorage.getItem(HIDDEN_CAPABILITIES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Storage is attacker-visible and version-skewed: keep only the strings so
    // the declared string[] contract holds for callers.
    return parsed.filter((value): value is string => typeof value === 'string');
  } catch {
    return [];
  }
}

export const useNavLinksList = () => {
  const { formatMessage } = useIntl();
  // A non-impersonating caregiver gets a limited (empty) nav instead of the
  // full member nav. While the caregiver claim is still resolving
  // (`isCaregiverShell === null`) we also withhold the member nav, so a
  // caregiver never briefly sees member links before the claim lands. Members
  // get the full list only once `isCaregiverShell` is explicitly `false`.
  const isCaregiverShell = useIsCaregiverShell();

  const hiddenCapabilities = getHiddenCapabilities();

  const allNavLinks: NavLinkEntry[] = [
    {
      to: APP_MANAGED_ROUTES.HOME,
      label: formatMessage({ id: 'STR_HOME' }),
      icon: 'descriptiveHome',
    },
    {
      to: getModulePath(LEAGUE_MODULE_NAMES.benefits),
      label: formatMessage({ id: 'STR_BENEFITS' }),
      icon: 'capabilityWallet',
      capabilityKey: LEAGUE_MODULE_NAMES.benefits,
    },
    {
      to: getModulePath(LEAGUE_MODULE_NAMES.careDiscovery),
      label: formatMessage({ id: 'STR_GET_CARE' }),
      icon: 'descriptivePill',
      capabilityKey: LEAGUE_MODULE_NAMES.careDiscovery,
    },
    {
      to: getModulePath(LEAGUE_MODULE_NAMES.healthJourney),
      label: formatMessage({ id: 'STR_JOURNEY' }),
      icon: 'capabilityJourney',
      capabilityKey: LEAGUE_MODULE_NAMES.healthJourney,
    },
  ];

  const navLinksList: HeaderNavLink[] = allNavLinks
    .filter(
      ({ capabilityKey }) =>
        !capabilityKey || !hiddenCapabilities.includes(capabilityKey),
    )
    .map(({ capabilityKey: _, ...link }) => link);

  return {
    navLinks: isCaregiverShell !== false ? [] : navLinksList,
  };
};
